import React, { useState, useRef, useEffect } from "react";
import { RiFileEditLine } from "react-icons/ri";
import useTitanApp from "../../../../../hooks/useTitanApp";
import {
  setPatientSummary,
  setpatientSummaryLoader,
  setIsSummaryGenerated,
  addupdateConsultationSummary,
  setSelectedPatientConsultation,
  setIsSummaryStreaming,
  setReferralLetter,
} from "../../../../../dispatchers/patientDispatchers";
import AiStreamResponse from "../../../../../utils/AiStreamResponse";
import { generateSummaryUrl } from "../../../../../apiUrls/apiUrls";
import { setStreamedText } from "../../../../../dispatchers/templateDispatchers";

export default function FileEditPopover({ isDarkMode }) {
  const { templateState, patientState } = useTitanApp();
  const { selectedTemplate } = templateState || {};
  const {
    patientTranscription,
    selectedPatientConsultation,
    isFinalSummary,
    PatientNotes,
    patientSummary,
    consultationId,
    isSummaryStreaming,
  } = patientState || {};

  const [isOpen, setIsOpen] = useState(false);
  const popoverRef = useRef(null);
  const [errorMsg, setErrorMsg] = useState("");
  // const [streamedText, setStreamedText] = useState("");
  const accumulatedText = useRef("");
  const shownKeys = useRef(new Set());
  const queue = useRef([]);
  const typingInterval = useRef(null);
  const buffer = useRef("");

  const defaultInstructions = selectedTemplate?.consultationSummary || "";

  const [summaryInstructions, setSummaryInstructions] =
    useState(defaultInstructions);
  const sessionOriginalRef = useRef(summaryInstructions);

  useEffect(() => {
    if (!isOpen) {
      const freshDefault =
        selectedTemplate?.consultationSummary ||
        "Generate a comprehensive consultation summary including chief complaint, history of present illness, examination findings, assessment, and plan.";
      setSummaryInstructions(freshDefault);
      sessionOriginalRef.current = freshDefault;
    }
  }, [selectedTemplate]);

  const togglePopover = () => {
    if (selectedPatientConsultation?.consultationStatus === 1) return;
    const next = !isOpen;
    setIsOpen(next);
    if (next) {
      sessionOriginalRef.current = summaryInstructions;
    }
  };

  useEffect(() => {
    if (!isOpen) return;
    function handleClickOutside(e) {
      if (popoverRef.current && !popoverRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  const extractNewTextIncremental = (buf, seenKeys) => {
    const regex = /"([^"]+)":\s*"([^"]*)"/g;
    let match;
    let output = "";
    let lastIndex = 0;

    while ((match = regex.exec(buf)) !== null) {
      const key = match[1];
      const value = match[2];

      if (key === "Actions") continue;

      if (!seenKeys.has(key)) {
        output += `${key}: ${value}\n\n`;
        seenKeys.add(key);
      }
      lastIndex = regex.lastIndex;
    }

    return {
      text: output,
      remainingBuffer: buf.slice(lastIndex),
    };
  };

  const startTypingEffect = () => {
    if (typingInterval.current) return;
    typingInterval.current = setInterval(() => {
      if (queue.current.length > 0) {
        const nextChar = queue.current.shift();
        setStreamedText((prev) => prev + nextChar);
      } else {
        clearInterval(typingInterval.current);
        typingInterval.current = null;
      }
    }, 15);
  };

  const getData = async () => {
    setIsSummaryStreaming(true);
    setpatientSummaryLoader(true);
    accumulatedText.current = "";
    shownKeys.current = new Set();
    setStreamedText("");
    queue.current = [];
    buffer.current = "";

    const updatedParams = {
      transcription: patientTranscription,
      serviceSummary: summaryInstructions,
      isFinalSummary,
      notes: PatientNotes,
    };
    return AiStreamResponse(
      updatedParams,
      // onChunk
      (chunk) => {
        accumulatedText.current += chunk;
        buffer.current += chunk;

        const { text: newText, remainingBuffer } = extractNewTextIncremental(
          buffer.current,
          shownKeys.current,
        );

        if (newText) {
          queue.current.push(...newText);
          startTypingEffect();
        }

        buffer.current = remainingBuffer;
      },
      // onDone
      () => {
        setpatientSummaryLoader(false);
        clearInterval(typingInterval.current);
        typingInterval.current = null;

        // Strip markdown code fences (e.g. ```json ... ```) that the AI sometimes returns
        accumulatedText.current = accumulatedText.current
          .replace(/^```(?:json)?\s*/i, "")
          .replace(/\s*```\s*$/, "")
          .trim();
        let isJson = false;
        try {
          JSON.parse(accumulatedText.current);
          isJson = true;
        } catch {
          isJson = false;
        }

        if (isJson) {
          try {
            const finalParsed = JSON.parse(accumulatedText.current);
            const formattedText = Object.entries(finalParsed)
              .filter(([key]) => key !== "Actions")
              .map(([key, value]) => `${key}:\n${value}`)
              .join("\n\n");

            // Save full object + computed formatted text
            setPatientSummary({
              ...finalParsed,
              SummaryText: formattedText,
            });

            addupdateConsultationSummary({
              consultationId: consultationId,
              summary: formattedText,
            });
            setStreamedText(formattedText);
          } catch (err) {
            console.error("Failed to parse JSON response:", err);
            setStreamedText(accumulatedText.current);
          }
        } else {
          setStreamedText(accumulatedText.current);
        }

        setIsSummaryStreaming(false);
        setpatientSummaryLoader(false);
      },
      generateSummaryUrl(),
    ).finally(() => {
      setpatientSummaryLoader(false);
      setIsSummaryStreaming(false);
    });
  };

  const handleRegenerate = async () => {
    if (patientTranscription !== "" && summaryInstructions !== "") {
      // setSelectedPatientConsultation(null);

      setReferralLetter("");
      setPatientSummary("");
      await getData();
      setIsSummaryGenerated(true);
    } else if (patientSummary) {
      // Fallback: if we already have a patientSummary object, re-display it
      if (typeof patientSummary === "object") {
        if (patientSummary.SummaryText) {
          setStreamedText(patientSummary.SummaryText);
        } else {
          const summaryText = Object.entries(patientSummary)
            .filter(([k]) => k !== "Actions" && k !== "SummaryText")
            .map(([k, v]) => `${k}: \n${v}`)
            .join("\n\n");
          setStreamedText(summaryText);
        }
      }
    }
  };

  const handleApplyAndRegenerate = () => {
    if (!patientTranscription?.trim()) {
      setErrorMsg("Transcription is missing. Please add transcription first.");
      return;
    }
    setErrorMsg(""); // clear any old error
    handleRegenerate();
  };

  const handleReset = () => {
    setSummaryInstructions(sessionOriginalRef.current);
  };

  return (
    <div className="popover-container" ref={popoverRef}>
      <button
        className="popover-trigger"
        onClick={togglePopover}
        style={{
          "--border-color": isDarkMode ? "#4b5563" : "#d1d5db",
          "--btn-bg": isDarkMode ? "#1f2937" : "#fff",
          "--btn-hover-bg": isDarkMode ? "#374151" : "#f9fafb",
        }}
        title="Edit summary instructions"
      >
        <RiFileEditLine className="icons-color" />
      </button>

      <div
        className={`popover-content ${!isOpen ? "hidden" : ""}`}
        style={{
          "--border-color": isDarkMode ? "#4b5563" : "#d1d5db",
          "--popover-bg": isDarkMode ? "#1f2937" : "#fff",
          "--popover-text": isDarkMode ? "#fff" : "#111827",
          "--primary-color": "#2563eb",
        }}
      >
        <h4>Summary Instructions</h4>
        <p>Template: {selectedTemplate?.templateName || "Default Template"}</p>

        <textarea
          placeholder="Generate a comprehensive consultation summary including chief complaint, history of present illness, examination findings, assessment, and plan."
          value={summaryInstructions}
          onChange={(e) => setSummaryInstructions(e.target.value)}
          style={{
            backgroundColor: isDarkMode ? "#000" : "#fff",
            color: isDarkMode ? "#fff" : "#000",
          }}
          rows={8}
        />

        {errorMsg && (
          <div
            className="error-msg"
            style={{ color: "red", marginBottom: "0.5rem" }}
          >
            {errorMsg}
          </div>
        )}
        <div className="btn-row">
          <button
            className={`apply ${isDarkMode ? "dark" : "light"}`}
            onClick={handleApplyAndRegenerate}
            disabled={isSummaryStreaming || !summaryInstructions.trim()}
          >
            {isSummaryStreaming ? "Regenerating..." : "Apply & Regenerate"}
          </button>
          <button
            className={`reset ${isDarkMode ? "dark" : "light"}`}
            onClick={handleReset}
            disabled={isSummaryStreaming}
          >
            Reset to Default
          </button>
        </div>
      </div>
    </div>
  );
}
